import React from 'react';
import { EmptyState, SparkIcon } from '@spark';
import TaskPageTabEmpty from '@/features/tasks/components/TaskPageTabEmpty';

export const DEFAULT_EMPTY_TITLE = 'An overview will appear here';
export const DEFAULT_EMPTY_BODY = 'Once you have models, statistics will appear here.';

export const TRAINING_STOPPED_TITLE = 'Training run stopped';
export const TRAINING_STOPPED_BODY =
    "We halted the evaluation. Whenever you're ready to see how your model performs, just kick off a fresh training run.";
export const RETRAIN_LABEL = 'Re-train';

export const EVALUATION_FAILED_TITLE = 'Evaluation failed';
export const EVALUATION_FAILED_BODY =
    'We ran into an unexpected issue while calculating your results. Please try running the evaluation again.';
export const TRY_AGAIN_LABEL = 'Try Again';

/**
 * Figma 9756:7617 "You stopped it" (stopped) and 9756:7775 "We failed" (failed).
 *
 * Both frames are a 368px column: illustration → 16 → 18/24 title → 8 → 14/22 body
 * → 16 → a 36px primary button. Stopped reuses the task header's brain-icon
 * "Re-train"; failed is a plain "Try Again" with no icon and no reason line.
 */
const OUTCOME_VARIANTS = {
    stopped: {
        illustration: 'illustration-training-stopped',
        illustrationWidth: 272,
        illustrationHeight: 212,
        title: TRAINING_STOPPED_TITLE,
        subtitle: TRAINING_STOPPED_BODY,
        ctaLabel: RETRAIN_LABEL,
        ctaIconSlug: 'brain-icon',
    },
    failed: {
        illustration: 'illustration-evaluation-failed',
        illustrationWidth: 207,
        illustrationHeight: 234,
        title: EVALUATION_FAILED_TITLE,
        subtitle: EVALUATION_FAILED_BODY,
        ctaLabel: TRY_AGAIN_LABEL,
    },
};

const CONTENT_WIDTH = 368;
const CTA_SIZE = 'medium';

/**
 * Evaluations tab empty state — shared TaskPageTabEmpty chrome as Model Summary
 * / Data Editor (Figma 3001-71663).
 *
 * When the task has no model versions and its last training run was stopped
 * or failed, renders the matching Figma variant (9756:7617 / 9756:7775) with
 * a CTA that opens the existing Retrain flow.
 */
const EvaluationEmptyState = ({ outcome = null, onRetrain } = {}) => {
    const variant = OUTCOME_VARIANTS[outcome];
    if (!variant) {
        return (
            <TaskPageTabEmpty>
                <EmptyState title={DEFAULT_EMPTY_TITLE} subtitle={DEFAULT_EMPTY_BODY} showSubtitle />
            </TaskPageTabEmpty>
        );
    }

    return (
        <TaskPageTabEmpty>
            <EmptyState
                className={`evaluation-empty-state evaluation-empty-state--${outcome}`}
                size="large"
                illustration={variant.illustration}
                illustrationWidth={variant.illustrationWidth}
                illustrationHeight={variant.illustrationHeight}
                contentWidth={CONTENT_WIDTH}
                title={variant.title}
                subtitle={variant.subtitle}
                showMainButton
                ctaLabel={variant.ctaLabel}
                ctaSize={CTA_SIZE}
                ctaStartIcon={
                    variant.ctaIconSlug ? <SparkIcon slug={variant.ctaIconSlug} size="medium" /> : undefined
                }
                ctaDisableWhenReadOnly
                onCtaClick={onRetrain}
            />
        </TaskPageTabEmpty>
    );
};

export default EvaluationEmptyState;
